import { useState } from "react";
import {
  Text,
  Pressable,
  ActivityIndicator,
} from "react-native";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";
import { SafeAreaView } from "react-native-safe-area-context";
import styled from "styled-components/native";
import { useAuth } from "@/contexts/AuthContext";
import { createClub } from "@/lib/services";
import { colors, radius, font } from "@/lib/theme";

const SafeArea = styled(SafeAreaView)`
  flex: 1;
  background-color: ${colors.white};
`;

const Content = styled.View`
  flex: 1;
  padding-horizontal: 24px;
  padding-top: 24px;
  max-width: 480px;
  width: 100%;
  align-self: center;
`;

const Title = styled.Text`
  font-size: 24px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  letter-spacing: -0.5px;
  margin-bottom: 8px;
`;

const Subtitle = styled.Text`
  font-size: 15px;
  color: ${colors.inkSecondary};
  line-height: 22px;
  margin-bottom: 28px;
`;

const Label = styled.Text`
  font-size: 11px;
  font-weight: ${font.bold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
  margin-top: 16px;
`;

const Input = styled.TextInput`
  background-color: ${colors.bg};
  border-width: 2px;
  border-color: ${colors.line};
  border-radius: ${radius.card}px;
  padding-horizontal: 16px;
  padding-vertical: 14px;
  font-size: 16px;
  color: ${colors.ink};
`;

const Hint = styled.Text`
  font-size: 13px;
  color: ${colors.inkMuted};
  margin-top: 6px;
  line-height: 18px;
`;

const ErrorText = styled.Text`
  color: ${colors.danger};
  font-size: 14px;
  font-weight: ${font.semibold};
  margin-top: 12px;
`;

const TeamChips = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const TeamChip = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 6px;
  background-color: ${colors.brand50};
  border-width: 1px;
  border-color: ${colors.brandLight};
  border-radius: ${radius.full}px;
  padding-horizontal: 14px;
  padding-vertical: 8px;
`;

const TeamChipText = styled.Text`
  font-size: 14px;
  font-weight: ${font.semibold};
  color: ${colors.brandDark};
`;

const TeamChipRemove = styled.Text`
  font-size: 16px;
  color: ${colors.inkMuted};
  font-weight: ${font.bold};
`;

const AddTeamRow = styled.View`
  flex-direction: row;
  gap: 8px;
  margin-top: 10px;
`;

const AddTeamInput = styled.TextInput`
  flex: 1;
  background-color: ${colors.bg};
  border-width: 2px;
  border-color: ${colors.line};
  border-radius: ${radius.card}px;
  padding-horizontal: 14px;
  padding-vertical: 10px;
  font-size: 15px;
  color: ${colors.ink};
`;

const AddBtnText = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.brand};
`;

export default function CreateClub() {
  const { user } = useAuth();

  const [name, setName] = useState("");
  const [shortName, setShortName] = useState("");
  const [teams, setTeams] = useState<string[]>(["1st XI", "2nd XI"]);
  const [newTeam, setNewTeam] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const addTeam = () => {
    const trimmed = newTeam.trim();
    if (!trimmed) return;
    if (teams.includes(trimmed)) {
      setNewTeam("");
      return;
    }
    setTeams([...teams, trimmed]);
    setNewTeam("");
  };

  const removeTeam = (index: number) => {
    setTeams(teams.filter((_, i) => i !== index));
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      setError("Club name is required.");
      return;
    }
    if (!user) return;

    setLoading(true);
    setError("");

    try {
      const finalTeams = teams.length > 0 ? teams : ["Team"];

      const finalShortName = shortName.trim() || name
        .trim()
        .split(/\s+/)
        .map((w) => w[0])
        .join("")
        .toUpperCase();

      await createClub({
        name: name.trim(),
        shortName: finalShortName,
        teams: finalTeams,
        userId: user.uid,
        userEmail: user.email || "",
        userName: user.displayName || user.email?.split("@")[0] || "",
      });

      // ClubContext will detect the new membership and NavigationGate
      // will redirect to (app) automatically
    } catch (err: any) {
      setError(err.message || "Something went wrong.");
      setLoading(false);
    }
  };

  return (
    <SafeArea>
        <KeyboardAwareScroll contentContainerStyle={{ flexGrow: 1 }} keyboardShouldPersistTaps="handled">
          <Content>
            <Title>Create your club</Title>
            <Subtitle>You'll be the admin. You can invite others after.</Subtitle>

            <Label>Club name</Label>
            <Input
              placeholder="e.g. Peterborough Knights Cricket Club"
              placeholderTextColor={colors.inkMuted}
              value={name}
              onChangeText={setName}
              autoCapitalize="words"
              autoFocus
            />

            <Label>Short name (optional)</Label>
            <Input
              placeholder="e.g. PKCC"
              placeholderTextColor={colors.inkMuted}
              value={shortName}
              onChangeText={setShortName}
              autoCapitalize="characters"
            />

            <Label>Teams</Label>
            <TeamChips>
              {teams.map((team, i) => (
                <TeamChip key={i}>
                  <TeamChipText>{team}</TeamChipText>
                  <Pressable onPress={() => removeTeam(i)} hitSlop={8}>
                    <TeamChipRemove>×</TeamChipRemove>
                  </Pressable>
                </TeamChip>
              ))}
            </TeamChips>

            <AddTeamRow>
              <AddTeamInput
                placeholder="Add a team..."
                placeholderTextColor={colors.inkMuted}
                value={newTeam}
                onChangeText={setNewTeam}
                autoCapitalize="words"
                onSubmitEditing={addTeam}
                returnKeyType="done"
              />
              <Pressable
                onPress={addTeam}
                style={({ pressed }) => [
                  {
                    backgroundColor: colors.brand50,
                    borderWidth: 2,
                    borderColor: colors.brandLight,
                    borderRadius: radius.card,
                    paddingHorizontal: 16,
                    justifyContent: "center" as const,
                  },
                  pressed && { backgroundColor: colors.brandLight },
                ]}
              >
                <AddBtnText>Add</AddBtnText>
              </Pressable>
            </AddTeamRow>
            <Hint>You can add or rename teams later in club settings.</Hint>

            {error ? <ErrorText>{error}</ErrorText> : null}

            <Pressable
              style={({ pressed }) => [
                {
                  backgroundColor: colors.brand,
                  borderRadius: radius.card,
                  paddingVertical: 16,
                  alignItems: "center" as const,
                  marginTop: 28,
                  shadowColor: colors.brand,
                  shadowOffset: { width: 0, height: 4 },
                  shadowOpacity: 0.25,
                  shadowRadius: 14,
                  elevation: 4,
                },
                pressed && { opacity: 0.9 },
              ]}
              onPress={handleCreate}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={{ fontSize: 16, fontWeight: "700", color: "#fff" }}>
                  Create club →
                </Text>
              )}
            </Pressable>
          </Content>
        </KeyboardAwareScroll>
    </SafeArea>
  );
}
